import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Draggable } from 'react-beautiful-dnd';
import { LeadCalendarIcon } from '../LeadCalendarIcon';
import { Content, MainContent, Container } from './styles';
import { 
  LeadHeader, 
  LeadInfoSection, 
  LeadProgress, 
  CopyLeadButton,
  UserAvatar
} from './components';

const DraggableCard = memo(({ props, item, showDrawer, columnId, showOnboarding, isFromForm }) => {
  const { provided, snapshot } = props;
  const { innerRef, draggableProps, dragHandleProps } = provided;
  const { isDragging } = snapshot;
  
  const isRevised = useMemo(() => {
    const additionalDataRevised = item.additional_data && Boolean(item.additional_data.is_revised);
    const itemRevised = Boolean(item.is_revised);
    return additionalDataRevised || itemRevised;
  }, [item.additional_data, item.is_revised]);
  
  const handleClick = useMemo(() => () => showDrawer({ id: item.id }), [showDrawer, item.id]);

  return (
    <Container
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
      isDragging={isDragging}
      isRevised={isRevised}
      onClick={handleClick}
      className="lead-card-container"
    >
      <LeadHeader item={item} />
      <CopyLeadButton item={item} />
      <Content>
        <MainContent>
          <LeadInfoSection item={item} isFromForm={isFromForm} />
          {item.stage !== 1 && item.stage !== 3 && item.stage !== 4 && <LeadCalendarIcon item={item} />}
        </MainContent>
      </Content>
      <UserAvatar assignedTo={item.assigned_to} />
      <LeadProgress percentage={item.percentage} />
    </Container>
  );
});

export const LeadCard = memo(({ index, item, showDrawer, columnId, showOnboarding, isFromForm }) => (
  <Draggable key={index} draggableId={String(item.id)} index={index}>
    {(provided, snapshot) => (
      <DraggableCard
        props={{ provided, snapshot }}
        item={item}
        showDrawer={showDrawer}
        columnId={columnId}
        showOnboarding={showOnboarding}
        isFromForm={isFromForm}
      />
    )}
  </Draggable>
));

LeadCard.propTypes = {
  index: PropTypes.number.isRequired,
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    created_at: PropTypes.string,
    honorary: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    source: PropTypes.string,
    company_type: PropTypes.string,
    necessity: PropTypes.number,
    rate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    stage: PropTypes.number,
    percentage: PropTypes.number,
    additional_data: PropTypes.object,
    is_revised: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  }).isRequired,
  showDrawer: PropTypes.func.isRequired,
  columnId: PropTypes.number,
  showOnboarding: PropTypes.bool,
  isFromForm: PropTypes.bool,
};

// For backward compatibility, also export as named export
export function LeadCardComponent(props) {
  return <LeadCard {...props} />;
}
